import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TablePagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { ApiContext } from '../contexts/ApiContext';

const PaiFrequentAccounts = () => {
  const { apiCall } = useContext(ApiContext);
  const [activeTab, setActiveTab] = useState(0);
  const [frequentAccounts, setFrequentAccounts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPage, setSearchPage] = useState(0);
  const [searchRowsPerPage, setSearchRowsPerPage] = useState(25);
  const [searchTotal, setSearchTotal] = useState(0);

  useEffect(() => {
    fetchFrequentAccounts();
  }, []);

  const fetchFrequentAccounts = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/v1/pai/frequent-accounts');
      console.log('Frequent accounts API response:', response);
      setFrequentAccounts(response.accounts || []);
      setError(null);
    } catch (err) {
      setError('Failed to load frequent accounts');
      console.error('Error fetching frequent accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchAccounts = async (page = searchPage) => {
    if (searchQuery.length < 2) {
      setError('Please enter at least 2 characters to search');
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);
      const response = await apiCall('/api/v1/pai/frequent-accounts/search', 'GET', null, {
        q: searchQuery,
        limit: searchRowsPerPage,
        offset: page * searchRowsPerPage
      });
      
      console.log('Search results:', response);
      setSearchResults(response.accounts || []);
      setSearchTotal(response.total || 0);
    } catch (err) {
      setError('Failed to search accounts');
      console.error('Error searching accounts:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchPage(0);
    searchAccounts(0);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchPage(0);
      searchAccounts(0);
    }
  };

  const handleAddToFrequent = async (account) => {
    try {
      // Use route as-is (handles both 9-digit and masked *****1234 formats)
      await apiCall('/api/v1/pai/frequent-accounts', 'POST', {
        nameOnAccount: account.name_on_account,
        route: account.route,
        account: account.account
      });
      
      // Refresh both lists
      await fetchFrequentAccounts();
      await searchAccounts();
      
      // Show success
      setError(null);
    } catch (err) {
      if (err.message?.includes('already in your frequent accounts')) {
        setError('This account is already in your frequent accounts');
      } else {
        setError('Failed to add account to frequent list');
      }
      console.error('Error adding to frequent:', err);
    }
  };

  const handleRemoveFromFrequent = async (accountId) => {
    if (!window.confirm('Remove this account from your frequent list?')) {
      return;
    }
    
    try {
      await apiCall(`/api/v1/pai/frequent-accounts/${accountId}`, 'DELETE');
      await fetchFrequentAccounts();
      // Refresh search if active
      if (searchResults.length > 0) {
        await searchAccounts();
      }
      setError(null);
    } catch (err) {
      setError('Failed to remove account');
      console.error('Error removing account:', err);
    }
  };

  const handleSearchPageChange = (event, newPage) => {
    setSearchPage(newPage);
    searchAccounts();
  };

  const handleSearchRowsPerPageChange = (event) => {
    setSearchRowsPerPage(parseInt(event.target.value, 10));
    setSearchPage(0);
    searchAccounts();
  };

  if (loading && frequentAccounts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary">
        Frequent Accounts
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Search existing accounts and star them for quick access
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={`My Frequent Accounts (${frequentAccounts.length})`} />
          <Tab label="Search & Add Accounts" />
        </Tabs>
      </Paper>
      
      {activeTab === 0 && (
        <Box>
          {frequentAccounts.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>#</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Routing</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell align="center" width={80}>Remove</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {frequentAccounts.map((account, index) => (
                    <TableRow key={account.id} hover>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <StarIcon color="warning" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight="600">
                            {account.display_name || account.name_on_account}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {account.route}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ****{account.account && account.account.slice(-4)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove from frequent accounts">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFromFrequent(account.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <StarIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="text.secondary">
                No Frequent Accounts Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Switch to the "Search & Add Accounts" tab to find and star accounts
              </Typography>
            </Paper>
          )}
        </Box>
      )}
      
      {activeTab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
            <form onSubmit={handleSearch}>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  placeholder="Search by name, routing number, or account number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  size="small"
                  sx={{ flex: 1, maxWidth: 600 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Enter at least 2 characters
                </Typography>
              </Box>
            </form>
          </Paper>
          
          {searchLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : searchResults.length > 0 ? (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account Name</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Routing</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell align="center">Terminals</TableCell>
                      <TableCell align="center" width={80}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((account) => (
                      <TableRow key={`${account.name_on_account}-${account.route}-${account.account}`} hover>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {account.name_on_account}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {account.location}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {account.route}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            ****{account.account && account.account.slice(-4)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={account.terminal_count}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {account.is_frequent ? (
                            <Tooltip title="Already in frequent accounts">
                              <IconButton size="small" disabled>
                                <StarIcon color="warning" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Add to frequent accounts">
                              <IconButton
                                size="small"
                                onClick={() => handleAddToFrequent(account)}
                                color="primary"
                              >
                                <StarBorderIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={searchTotal}
                  page={searchPage}
                  onPageChange={handleSearchPageChange}
                  rowsPerPage={searchRowsPerPage}
                  onRowsPerPageChange={handleSearchRowsPerPageChange}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </TableContainer>
            </>
          ) : searchQuery.length >= 2 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="text.secondary">
                No Accounts Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="text.secondary">
                Search for Accounts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter at least 2 characters to search for existing accounts
              </Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PaiFrequentAccounts;